import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  FiX,
  FiUsers,
  FiCalendar,
  FiMail,
  FiEye,
  FiMousePointer,
  FiAtSign,
  FiSend,
  FiFileText,
  FiTrash2,
} from "react-icons/fi";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { mono, eyebrow, getColorFromText } from "../styles";
import TemplatePreviewFrame from "./TemplatePreviewFrame";

const slideIn = keyframes`
  from { transform: translateX(32px); opacity: 0; }
  to   { transform: translateX(0); opacity: 1; }
`;
const overlayFade = keyframes`from { opacity: 0; } to { opacity: 1; }`;

// Renderizado via portal direto em document.body (ver final do arquivo) para
// escapar de qualquer stacking context de ancestrais (ex.: a status bar
// flutuante do app, que é position:fixed em outro ponto da árvore) — assim
// o z-index sempre compara corretamente no nível raiz do documento.
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(8px) saturate(130%);
  display: flex;
  justify-content: flex-end;
  z-index: 10001;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};
`;

const Drawer = styled.div`
  width: 100%;
  max-width: 560px;
  height: 100%;
  background: ${({ theme }) => surface(theme, 0)};
  border-left: 1px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: -24px 0 60px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  animation: ${slideIn} ${tokens.durSlow} ${tokens.easeOut};
`;

const DrawerHeader = styled.div`
  padding: 20px 22px 16px;
  border-bottom: 1px solid ${({ theme }) => hairline(theme)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  flex-shrink: 0;

  .title-block {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
    flex: 1;

    .eyebrow { ${eyebrow}; font-size: 0.58em; opacity: 0.4; }

    h3 {
      margin: 0;
      font-size: 1.12em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.015em;
      line-height: 1.3;
    }

    .subject {
      margin: 2px 0 0 0;
      font-size: 0.76em;
      font-style: italic;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .status-pill {
    ${mono};
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: ${tokens.radiusPill};
    font-size: 0.6em;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: ${tokens.brandSoft};
    color: ${tokens.brand};
    border: 1px solid ${tokens.brandRing};
    white-space: nowrap;
  }

  .delete-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    cursor: pointer;
    flex-shrink: 0;

    svg { font-size: 1em; }
    &:hover { opacity: 1; border-color: rgba(239, 68, 68, 0.3); color: ${tokens.danger}; background: ${tokens.dangerSoft}; }
  }

  .close-btn {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => surface(theme, 1)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    color: ${({ theme }) => theme.text};
    opacity: 0.65;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};
    flex-shrink: 0;

    svg { font-size: 1em; }
    &:hover { opacity: 1; border-color: ${({ theme }) => hairline(theme, true)}; }
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => hairline(theme, true)}; border-radius: 3px; }
`;

/* ---------- Card de seção ---------- */
const Section = styled.section`
  background: ${({ theme }) => surface(theme, 1)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusLg};
  padding: 13px 15px 15px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    .section-title {
      ${eyebrow};
      font-size: 0.6em;
      opacity: 0.55;
      display: inline-flex;
      align-items: center;
      gap: 7px;

      svg { font-size: 1.15em; color: ${tokens.brand}; opacity: 0.9; }
    }

    .section-hint {
      ${mono};
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      text-align: right;
    }
  }
`;

/* ---------- Meta (chips) ---------- */
const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 9px 11px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    min-width: 0;

    svg {
      font-size: 0.95em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      flex-shrink: 0;
    }

    .meta-text {
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;

      .meta-label {
        ${mono};
        font-size: 0.55em;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: ${({ theme }) => theme.text};
        opacity: 0.45;
      }

      .meta-value {
        font-size: 0.78em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }
`;

/* ---------- Métricas ---------- */
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;

  .stat {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 11px 12px;
    background: ${({ theme }) => surface(theme, 0)};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    min-width: 0;

    .stat-label {
      ${mono};
      font-size: 0.55em;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
      white-space: nowrap;
    }

    .stat-value {
      ${mono};
      font-size: 1.25em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      line-height: 1.1;
    }

    .stat-sub {
      ${mono};
      font-size: 0.62em;
      color: ${({ theme }) => theme.text};
      opacity: 0.45;
      white-space: nowrap;
    }

    &.open   { border-color: ${tokens.brandRing}; .stat-value { color: ${tokens.brand}; } .stat-sub { color: ${tokens.brand}; opacity: 0.75; } }
    &.click  { border-color: rgba(59, 130, 246, 0.28); .stat-value { color: ${tokens.info}; } .stat-sub { color: ${tokens.info}; opacity: 0.75; } }
    &.failed { .stat-value { color: ${tokens.danger}; } }
    &.failed.zero { .stat-value { color: ${({ theme }) => theme.text}; } }
  }

  @media (max-width: 480px) { grid-template-columns: repeat(2, 1fr); }
`;

/* ---------- Quem abriu ---------- */
const OpenersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const OpenerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusMd};
  transition: border-color ${tokens.durBase} ${tokens.easeOut};

  &:hover { border-color: ${({ theme }) => hairline(theme, true)}; }

  .opener-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-family: ${tokens.fontMono};
    font-size: 0.6em;
    font-weight: 700;
    flex-shrink: 0;
  }

  .opener-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;

    .opener-name {
      font-size: 0.8em;
      font-weight: 500;
      color: ${({ theme }) => theme.text};
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .opener-email {
      ${mono};
      font-size: 0.68em;
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .opener-badges {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;

    .badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 8px;
      border-radius: ${tokens.radiusPill};
      font-size: 0.62em;
      font-weight: 500;
      ${mono};
      white-space: nowrap;

      svg { font-size: 1em; }

      &.open-badge { background: ${tokens.brandSoft}; color: ${tokens.brand}; border: 1px solid ${tokens.brandRing}; }
      &.click-badge { background: ${tokens.infoSoft}; color: ${tokens.info}; border: 1px solid rgba(59, 130, 246, 0.25); }
    }
  }
`;

const EmptyState = styled.div`
  ${mono};
  font-size: 0.72em;
  color: ${({ theme }) => theme.text};
  opacity: 0.5;
  padding: 16px 12px;
  text-align: center;
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px dashed ${({ theme }) => hairline(theme, true)};
  border-radius: ${tokens.radiusMd};
`;

const formatDateTime = (d) =>
  d ? new Date(d).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—";

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
};

export default function CampaignDetailDrawer({ campaign, listName, onClose, fetchTemplate, fetchEvents, onDelete }) {
  const [htmlContent, setHtmlContent] = useState(campaign?.htmlContent || "");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [openers, setOpeners] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  useEffect(() => {
    if (!campaign) return;
    if (campaign.htmlContent) {
      setHtmlContent(campaign.htmlContent);
      return;
    }
    if (!fetchTemplate) return;

    let cancelled = false;
    setLoadingTemplate(true);
    fetchTemplate(campaign.id)
      .then((html) => {
        if (!cancelled) setHtmlContent(html);
      })
      .catch(() => {
        if (!cancelled) setHtmlContent("");
      })
      .finally(() => {
        if (!cancelled) setLoadingTemplate(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaign, fetchTemplate]);

  useEffect(() => {
    if (!campaign || !fetchEvents) return;

    let cancelled = false;
    setLoadingEvents(true);
    fetchEvents(campaign.id)
      .then((data) => {
        if (!cancelled) setOpeners(data.byContact || []);
      })
      .catch(() => {
        if (!cancelled) setOpeners([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });

    return () => {
      cancelled = true;
    };
  }, [campaign, fetchEvents]);

  if (!campaign) return null;

  const metrics = campaign.metrics || {};
  const openersWithActivity = openers.filter((o) => o.opens?.length > 0 || o.clicks?.length > 0);
  const failedCount = campaign.failedCount || 0;

  return ReactDOM.createPortal(
    <Overlay onClick={onClose}>
      <Drawer onClick={(e) => e.stopPropagation()}>
        <DrawerHeader>
          <div className="title-block">
            <span className="eyebrow">RELATÓRIO DA CAMPANHA</span>
            <h3>{campaign.name}</h3>
            {campaign.subject && <p className="subject" title={campaign.subject}>{campaign.subject}</p>}
          </div>
          <div className="header-right">
            <span className="status-pill">Enviada</span>
            {onDelete && (
              <button className="delete-btn" title="Excluir campanha" onClick={() => onDelete(campaign)}>
                <FiTrash2 />
              </button>
            )}
            <button className="close-btn" onClick={onClose}><FiX /></button>
          </div>
        </DrawerHeader>

        <DrawerBody>
          <Section>
            <div className="section-head">
              <span className="section-title"><FiSend /> RESUMO DO ENVIO</span>
            </div>

            <MetaGrid>
              <div className="meta-item">
                <FiCalendar />
                <div className="meta-text">
                  <span className="meta-label">Enviada em</span>
                  <span className="meta-value">{formatDateTime(campaign.sentAt)}</span>
                </div>
              </div>
              <div className="meta-item">
                <FiUsers />
                <div className="meta-text">
                  <span className="meta-label">Lista</span>
                  <span className="meta-value" title={listName}>{listName || "—"}</span>
                </div>
              </div>
              <div className="meta-item">
                <FiMail />
                <div className="meta-text">
                  <span className="meta-label">Destinatários</span>
                  <span className="meta-value">{campaign.recipientsCount || 0}</span>
                </div>
              </div>
              <div className="meta-item">
                <FiAtSign />
                <div className="meta-text">
                  <span className="meta-label">Remetente</span>
                  <span className="meta-value" title={`${campaign.senderName || ""} <${campaign.senderEmail || ""}>`}>
                    {campaign.senderName || campaign.senderEmail || "—"}
                  </span>
                </div>
              </div>
            </MetaGrid>

            <StatsGrid>
              <div className="stat">
                <span className="stat-label">Enviados</span>
                <span className="stat-value">{campaign.sentCount ?? campaign.recipientsCount ?? 0}</span>
                <span className="stat-sub">entregues</span>
              </div>
              <div className="stat open">
                <span className="stat-label">Aberturas</span>
                <span className="stat-value">{metrics.opens ?? 0}</span>
                <span className="stat-sub">{metrics.openRate ?? 0}% taxa</span>
              </div>
              <div className="stat click">
                <span className="stat-label">Cliques</span>
                <span className="stat-value">{metrics.clicks ?? 0}</span>
                <span className="stat-sub">{metrics.clickRate ?? 0}% taxa</span>
              </div>
              <div className={`stat failed ${failedCount === 0 ? "zero" : ""}`}>
                <span className="stat-label">Falhas</span>
                <span className="stat-value">{failedCount}</span>
                <span className="stat-sub">no envio</span>
              </div>
            </StatsGrid>
          </Section>

          <Section>
            <div className="section-head">
              <span className="section-title"><FiEye /> QUEM ABRIU / CLICOU</span>
              <span className="section-hint">{openersWithActivity.length} contato{openersWithActivity.length !== 1 ? "s" : ""}</span>
            </div>

            {loadingEvents ? (
              <EmptyState>Carregando interações...</EmptyState>
            ) : openersWithActivity.length === 0 ? (
              <EmptyState>Ninguém abriu ou clicou ainda</EmptyState>
            ) : (
              <OpenersList>
                {openersWithActivity.map((opener) => (
                  <OpenerRow key={opener.contactId}>
                    <span
                      className="opener-avatar"
                      style={{ background: getColorFromText(opener.email || opener.name || "?") }}
                    >
                      {getInitials(opener.name || opener.email)}
                    </span>
                    <div className="opener-info">
                      <span className="opener-name">{opener.name || "Contato"}</span>
                      <span className="opener-email">{opener.email || "—"}</span>
                    </div>
                    <div className="opener-badges">
                      {opener.opens?.length > 0 && (
                        <span
                          className="badge open-badge"
                          title={`Abriu em ${formatDateTime(opener.opens[opener.opens.length - 1])}`}
                        >
                          <FiEye /> {opener.opens.length}
                        </span>
                      )}
                      {opener.clicks?.length > 0 && (
                        <span
                          className="badge click-badge"
                          title={`Clicou em ${formatDateTime(opener.clicks[opener.clicks.length - 1]?.createdAt)}`}
                        >
                          <FiMousePointer /> {opener.clicks.length}
                        </span>
                      )}
                    </div>
                  </OpenerRow>
                ))}
              </OpenersList>
            )}
          </Section>

          <Section>
            <div className="section-head">
              <span className="section-title"><FiFileText /> TEMPLATE ENVIADO</span>
            </div>

            {loadingTemplate ? (
              <EmptyState>Carregando preview do template...</EmptyState>
            ) : (
              <TemplatePreviewFrame htmlContent={htmlContent} height={320} />
            )}
          </Section>
        </DrawerBody>
      </Drawer>
    </Overlay>,
    document.body
  );
}
